import { supabase } from '../supabase/client';
import { API_BASE_URL } from '../config/api';
import {
  extractFeaturesProcessed,
  extractPersonsDetected,
  normalizeAnalysisData,
  parseMetricNumber,
} from '../lib/analysisMetrics';

/**
 * Optimized Supabase service for video analysis operations
 * Uses efficient queries with selective column fetching
 */

// ✅ Global cache for dashboard data to avoid re-fetching
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  userId: string;
}

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache validity (reduced for faster updates)
const dashboardVideosCache = new Map<string, CacheEntry<DashboardVideoSummary[]>>();
const imageUrlCache = new Map<string, CacheEntry<string>>();

// Successful preview blobs/URLs — long TTL
const IMAGE_CACHE_DURATION = 60 * 60 * 1000; // 1 hour
// "No preview" misses — short TTL so new thumbnails appear after analysis completes
const NEGATIVE_PREVIEW_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

type ImageProxyCacheEntry = { url: string; timestamp: number; miss?: boolean };

// Cache cleanup function
function cleanupExpiredCache() {
  const now = Date.now();
  const expiredKeys: string[] = [];
  
  // Check dashboard videos cache
  dashboardVideosCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_DURATION) {
      expiredKeys.push(key);
    }
  });
  
  // Check image URL cache
  imageUrlCache.forEach((value, key) => {
    if (now - value.timestamp > IMAGE_CACHE_DURATION) {
      expiredKeys.push(`img_${key}`);
    }
  });
  
  expiredKeys.forEach(key => {
    if (key.startsWith('img_')) {
      imageUrlCache.delete(key.substring(4));
    } else {
      dashboardVideosCache.delete(key);
    }
  });
  
  return expiredKeys.length;
}

// Run cleanup every 10 minutes
let cleanupInterval: NodeJS.Timeout | null = null;
function startCacheCleanup() {
  if (!cleanupInterval) {
    cleanupInterval = setInterval(cleanupExpiredCache, 10 * 60 * 1000);
  }
}

function stopCacheCleanup() {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
}

export interface DashboardVideoSummary {
  id: string;
  video_id?: string;
  video_name: string;
  alias_name?: string | null; // User-defined display name
  request_id?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  video_duration_seconds?: number;
  video_size_mb?: number;
  preview_gcs_path?: string;
  preview_public_url?: string;
  processing_time_seconds?: number;
  video_platform?: string; // Platform: instagram, tiktok, youtube, upload, private
  video_source_type?: string; // Source type matching platform
  video_source_url?: string; // Original source URL
}

export interface RecentAnalysis extends DashboardVideoSummary {
  // Analysis scores would be in analysis_metadata JSONB
  // For now, we'll just use the base fields
}

export interface VideoDistribution {
  instagram: number;
  tiktok: number;
  youtube: number;
  x: number;
  custom: number;
}

export interface DashboardStats {
  total: number;
  completed: number;
  processing: number;
  failed: number;
  avgProcessingTime: number;
  totalAnalyses: number;
  completedCount: number;
  avgQualityScore: number;
  avgViralScore: number;
  videoDistribution: VideoDistribution;
  totalPersonsDetected: number;
  totalFeaturesProcessed: number;
  storageUsedMB: number;
}

export interface GetDashboardVideosOptions {
  offset?: number;
  limit?: number;
  status?: string;
  excludeForensic?: boolean;
}

export interface GetDashboardVideosResponse {
  data: DashboardVideoSummary[];
  count: number;
}

// Cache for image proxy URLs to avoid repeated fetch attempts
const imageProxyCache = new Map<string, ImageProxyCacheEntry>();

// Global URL cache to prevent duplicate timestamp generation
const globalUrlCache = new Map<string, string>();
const URL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch image with CORS handling via fetch with no-cors mode
 * This creates a blob that can be converted to a data URL for display.
 *
 * Returns:
 *   - { blob }                         when the response is a real image
 *   - { invalidContent: true }         when the response is OK but not an image
 *                                      (e.g. backend's JSON "no preview" payload)
 *   - { notFound: true }               when the server says there is no preview (404)
 *   - null                             when the fetch itself failed (network/CORS / other status)
 */
async function fetchImageAsBlob(
  imageUrl: string
): Promise<{ blob: Blob } | { invalidContent: true } | { notFound: true } | null> {
  try {
    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Accept': 'image/*',
      },
      credentials: 'omit', // Don't send credentials to avoid CORS preflight issues
      mode: 'cors',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { notFound: true };
      }
      return null;
    }

    // Backend returns 200 + application/json when no preview exists.
    // Treat that as "no image" so callers can show a placeholder instead of
    // handing JSON bytes to an <img> tag.
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (!contentType.startsWith('image/')) {
      return { invalidContent: true };
    }

    return { blob: await response.blob() };
  } catch (error) {
    console.warn(`Failed to fetch image from ${imageUrl}:`, error);
    return null;
  }
}

/**
 * Convert blob to data URL for CORS-safe display
 */
async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert a stored Supabase preview URL (or GCS path) into a publicly fetchable
 * https://storage.googleapis.com/... URL.
 *
 * Supabase stores preview URLs in the `storage.cloud.google.com/...` form,
 * which 302-redirects to a Google login page (so an <img> tag never renders).
 * The same object is publicly readable via `storage.googleapis.com/...`.
 *
 * Accepts either the full `preview_public_url` or the bare `preview_gcs_path`.
 * Returns null if the input is empty/unrecognized.
 */
export function toPublicGcsPreviewUrl(
  previewUrlOrPath: string | null | undefined
): string | null {
  if (!previewUrlOrPath) return null;

  const trimmed = previewUrlOrPath.trim();
  if (!trimmed) return null;

  // Already public — leave alone.
  if (trimmed.startsWith('https://storage.googleapis.com/')) {
    return trimmed;
  }

  // Login-required form → swap host.
  if (trimmed.startsWith('https://storage.cloud.google.com/')) {
    return trimmed.replace(
      'https://storage.cloud.google.com/',
      'https://storage.googleapis.com/'
    );
  }

  // Bare GCS path stored in `preview_gcs_path` (e.g. "video_frame/foo.jpg").
  if (!/^https?:\/\//i.test(trimmed)) {
    const cleanPath = trimmed.replace(/^\/+/, '');
    return `https://storage.googleapis.com/mentis_videos/${cleanPath}`;
  }

  return null;
}

/**
 * Get consistent thumbnail URL for a video to prevent duplicate requests
 * Uses global caching to ensure same URL is returned for same video
 * Also normalizes video names to prevent double extensions
 */
export function getConsistentThumbnailUrl(userId: string, videoName: string): string {
  // Normalize video name to prevent double extensions
  const normalizedVideoName = normalizeVideoName(videoName);
  const cacheKey = `${userId}:${normalizedVideoName}`;
  
  // Check if we already have a URL for this video
  const cached = globalUrlCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const timestamp = Date.now();
  const url = `${API_BASE_URL}/api/v1/users/video-preview/${userId}/${encodeURIComponent(normalizedVideoName)}?v=${timestamp}`;
  
  // Cache the URL for consistent usage
  globalUrlCache.set(cacheKey, url);
  
  // Clean up old cache entries after 5 minutes
  setTimeout(() => {
    globalUrlCache.delete(cacheKey);
  }, URL_CACHE_DURATION);
  
  return url;
}

/**
 * Normalize video name to prevent double extensions and other issues
 */
function normalizeVideoName(videoName: string): string {
  if (!videoName) return videoName;
  
  // Remove repeated extensions like .mp4.mp4, .json.json, etc.
  let normalized = videoName.replace(/(\.mp4)+$/i, '').replace(/(\.json)+$/i, '');
  
  // Remove other repeated video extensions
  normalized = normalized.replace(/(\.avi|\.mov|\.wmv|\.flv|\.mkv|\.webm|\.ogg|\.3gp)+$/i, '');
  
  return normalized;
}

/**
 * Get preview URL with CORS fallback handling
 * Uses multiple strategies to ensure images load correctly
 */
export async function getPreviewUrlWithFallback(
  userId: string,
  videoName: string
): Promise<string | null> {
  const cacheKey = `${userId}:${videoName}`;
  const apiPreviewUrl = getPreviewUrl(userId, videoName);

  // Check cache first
  const cached = imageProxyCache.get(cacheKey);
  if (cached) {
    const maxAge = cached.miss ? NEGATIVE_PREVIEW_CACHE_DURATION : IMAGE_CACHE_DURATION;
    if (Date.now() - cached.timestamp < maxAge) {
      if (cached.url) return cached.url;
      if (cached.miss) return null;
    } else {
      imageProxyCache.delete(cacheKey);
    }
  }

  // Use consistent URL to avoid duplicate requests
  const directImageUrl = getConsistentThumbnailUrl(userId, videoName);

  try {
    // Try to fetch the image and convert to blob URL to avoid CORS
    const result = await fetchImageAsBlob(directImageUrl);

    if (result && 'blob' in result) {
      // Convert blob to data URL (smaller, CORS-safe)
      const dataUrl = await blobToDataUrl(result.blob);

      // Cache the result
      imageProxyCache.set(cacheKey, { url: dataUrl, timestamp: Date.now() });

      return dataUrl;
    }

    if (result && 'invalidContent' in result) {
      imageProxyCache.set(cacheKey, { url: '', timestamp: Date.now(), miss: true });
      return apiPreviewUrl;
    }

    if (result && 'notFound' in result) {
      imageProxyCache.set(cacheKey, { url: '', timestamp: Date.now(), miss: true });
      return apiPreviewUrl;
    }
  } catch (error) {
    console.warn(`Failed to load image via blob proxy for ${videoName}:`, error);
  }

  // Transient failure: allow <img> to try the API proxy URL
  return directImageUrl;
}

/**
 * Get dashboard video summaries (lightweight query)
 * Only fetches necessary columns for dashboard display
 * ✅ Now uses global cache to avoid re-fetching on navigation
 */
export async function getDashboardVideos(
  userId: string,
  options: GetDashboardVideosOptions = {}
): Promise<GetDashboardVideosResponse> {
  const { offset = 0, limit = 100, status, excludeForensic = true } = options;
  
  // Generate cache key based on query parameters
  const cacheKey = `${userId}:${offset}:${limit}:${status || 'all'}:${excludeForensic ? 'no-forensic' : 'all'}`;
  
  // Check if we have valid cached data (within 5 minutes)
  const cached = dashboardVideosCache.get(cacheKey);
  if (cached && cached.userId === userId && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`✅ Using cached dashboard videos for ${userId}`);
    return { data: cached.data, count: cached.data.length };
  }

  let query = supabase
    .from('video_analysis_runs')
    .select('id,video_name,alias_name,video_id,request_id,status,created_at,updated_at,video_duration_seconds,video_size_mb,preview_gcs_path,preview_public_url,processing_time_seconds,video_platform,video_source_type,video_source_url', { count: 'exact' })
    .eq('firebase_uid', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (excludeForensic) {
    query = query.neq('video_source_type', 'forensic');
  }

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching dashboard videos:', error);
    // Return cached data if available, even if expired (better than empty)
    if (cached && cached.userId === userId) {
      console.warn(`Using stale cache due to error for ${userId}`);
      return { data: cached.data, count: cached.data.length };
    }
    return { data: [], count: 0 };
  }

  // Store in cache for subsequent calls
  if (data) {
    dashboardVideosCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      userId
    });
    startCacheCleanup(); // Ensure cleanup is running
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get recent analyses (completed analyses for dashboard)
 */
export async function getRecentAnalyses(
  userId: string,
  limit: number = 5
): Promise<RecentAnalysis[]> {
  
  return getDashboardVideos(userId, { limit, status: 'completed' }).then(
    response => response.data as RecentAnalysis[]
  );
}

async function fetchAnalysisForDashboardMetrics(
  userId: string,
  videoName: string
): Promise<Record<string, any> | null> {
  try {
    const { BillingService } = await import('./billingService');
    const record = await BillingService.getVideoAnalysis(userId, videoName);
    if (record?.analysis_data) {
      return normalizeAnalysisData(record.analysis_data);
    }
  } catch (error) {
    console.warn(`Dashboard metrics: Supabase RPC failed for ${videoName}`, error);
  }

  try {
    const url = `${API_BASE_URL}/api/v1/user/${userId}/bucket-files/${encodeURIComponent(videoName)}`;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const json = await response.json();
    return normalizeAnalysisData(json);
  } catch (error) {
    console.warn(`Dashboard metrics: API fallback failed for ${videoName}`, error);
    return null;
  }
}

function accumulateAnalysisMetrics(
  analysisData: unknown,
  totals: { persons: number; features: number }
): boolean {
  const persons = extractPersonsDetected(analysisData);
  const features = extractFeaturesProcessed(analysisData);
  totals.persons += persons;
  totals.features += features;
  return persons > 0 || features > 0;
}

/**
 * Get aggregated dashboard statistics
 * Uses a single query with counting
 */
export async function getDashboardStats(
  userId: string
): Promise<DashboardStats> {
  const { data, error } = await supabase
    .from('video_analysis_runs')
    .select('status,processing_time_seconds,video_platform,video_source_type,video_size_mb,analysis_metadata,video_name')
    .eq('firebase_uid', userId)
    .neq('video_source_type', 'forensic');

  if (error) {
    console.error('Error fetching dashboard stats:', error);
    return {
      total: 0,
      completed: 0,
      processing: 0,
      failed: 0,
      avgProcessingTime: 0,
      totalAnalyses: 0,
      completedCount: 0,
      avgQualityScore: 0,
      avgViralScore: 0,
      videoDistribution: { instagram: 0, tiktok: 0, youtube: 0, x: 0, custom: 0 },
      totalPersonsDetected: 0,
      totalFeaturesProcessed: 0,
      storageUsedMB: 0,
    };
  }

  // Get list of completed video names to fetch analysis data
  const completedVideoNames = (data || [])
    .filter((item: any) => item.status === 'completed' && item.video_name)
    .map((item: any) => item.video_name);

  const metricTotals = { persons: 0, features: 0 };
  const videosNeedingFallback = new Set<string>(completedVideoNames);

  (data || []).forEach((item: any) => {
    if (item.status !== 'completed' || !item.video_name || !item.analysis_metadata) {
      return;
    }

    try {
      const metadata = typeof item.analysis_metadata === 'string'
        ? JSON.parse(item.analysis_metadata)
        : item.analysis_metadata;

      const persons = parseMetricNumber(metadata.total_persons_detected);
      const features = parseMetricNumber(metadata.total_features_processed);

      if (persons > 0 || features > 0) {
        metricTotals.persons += persons;
        metricTotals.features += features;
        videosNeedingFallback.delete(item.video_name);
      }
    } catch (e) {
      console.warn('Failed to parse analysis_metadata:', e);
    }
  });

  if (completedVideoNames.length > 0) {
    try {
      const analysisDataMap = new Map<string, any>();

      const { data: analysisData, error: analysisError } = await supabase
        .from('video_analysis_storage')
        .select('video_name,analysis_data,created_at')
        .eq('user_id', userId)
        .in('video_name', completedVideoNames)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      if (!analysisError && analysisData) {
        analysisData.forEach((item: any) => {
          if (!analysisDataMap.has(item.video_name)) {
            analysisDataMap.set(item.video_name, item);
          }
        });

        analysisDataMap.forEach((item: any, videoName: string) => {
          if (!item.analysis_data) {
            return;
          }

          if (accumulateAnalysisMetrics(item.analysis_data, metricTotals)) {
            videosNeedingFallback.delete(videoName);
          }
        });
      }
    } catch (e) {
      console.warn('Error fetching analysis data from storage:', e);
    }
  }

  if (videosNeedingFallback.size > 0) {
    const fallbackResults = await Promise.all(
      Array.from(videosNeedingFallback).map(async (videoName) => ({
        videoName,
        analysis: await fetchAnalysisForDashboardMetrics(userId, videoName),
      }))
    );

    fallbackResults.forEach(({ analysis }) => {
      if (analysis) {
        accumulateAnalysisMetrics(analysis, metricTotals);
      }
    });
  }

  const totalPersonsDetected = metricTotals.persons;
  const totalFeaturesProcessed = metricTotals.features;

  const stats = (data || []).reduce(
    (acc: any, item: any) => {
      acc.total++;
      
      if (item.status === 'completed') {
        acc.completed++;
        if (item.processing_time_seconds) {
          acc.totalProcessingTime += item.processing_time_seconds;
          acc.processCount++;
        }
      } else if (item.status === 'processing' || item.status === 'pending') {
        acc.processing++;
      } else if (item.status === 'failed' || item.status === 'error') {
        acc.failed++;
      }

      const platform = item.video_platform || item.video_source_type || 'custom';
      if (platform === 'instagram' || platform.includes('instagram')) {
        acc.videoDistribution.instagram++;
      } else if (platform === 'tiktok' || platform.includes('tiktok')) {
        acc.videoDistribution.tiktok++;
      } else if (platform === 'youtube' || platform.includes('youtube')) {
        acc.videoDistribution.youtube++;
      } else if (platform === 'x' || platform === 'twitter' || platform.includes('x.com') || platform.includes('twitter')) {
        acc.videoDistribution.x++;
      } else {
        acc.videoDistribution.custom++;
      }

      if (item.video_size_mb) {
        acc.storageUsedMB += item.video_size_mb;
      }

      return acc;
    },
    {
      total: 0,
      completed: 0,
      processing: 0,
      failed: 0,
      totalProcessingTime: 0,
      processCount: 0,
      videoDistribution: { instagram: 0, tiktok: 0, youtube: 0, x: 0, custom: 0 },
      storageUsedMB: 0,
    }
  );

  return {
    total: stats.total,
    completed: stats.completed,
    processing: stats.processing,
    failed: stats.failed,
    avgProcessingTime:
      stats.processCount > 0
        ? Math.round(stats.totalProcessingTime / stats.processCount)
        : 0,
    totalAnalyses: stats.total,
    completedCount: stats.completed,
    avgQualityScore: 0,
    avgViralScore: 0,
    videoDistribution: stats.videoDistribution,
    totalPersonsDetected: totalPersonsDetected,
    totalFeaturesProcessed: totalFeaturesProcessed,
    storageUsedMB: Math.round(stats.storageUsedMB * 100) / 100,
  };
}

/**
 * Get detailed analysis for a specific video
 */
export async function getVideoAnalysisDetail(
  userId: string,
  videoName: string
): Promise<DashboardVideoSummary | null> {
  const { data, error } = await supabase
    .from('video_analysis_runs')
    .select('*')
    .eq('firebase_uid', userId)
    .eq('video_name', videoName)
    .single();

  if (error) {
    console.error('Error fetching video analysis detail:', error);
    return null;
  }

  return data;
}

/**
 * Detect video platform for a specific video using Supabase
 * Equivalent to: SELECT DISTINCT(video_platform) FROM video_analysis_runs 
 * WHERE firebase_uid = 'userId' AND video_name = 'videoName'
 */
export async function detectVideoPlatform(
  firebaseUid: string,
  videoName: string
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('video_analysis_runs')
      .select('video_platform')
      .eq('firebase_uid', firebaseUid)
      .eq('video_name', videoName)
      .not('video_platform', 'is', null);

    if (error) {
      console.error('Error detecting video platform:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    // Get distinct platforms (in case there are multiple entries)
    const platformValues = data.map((row: any) => row.video_platform).filter(Boolean) as string[];
    const platforms = Array.from(new Set(platformValues));
    
    if (platforms.length === 0) {
      return null;
    }

    // Return the first platform if multiple exist
    const detectedPlatform = platforms[0];
    
    return detectedPlatform;
  } catch (error) {
    console.error('Error in detectVideoPlatform:', error);
    return null;
  }
}

/**
 * Get all distinct video platforms for a user
 * Useful for analytics and filtering options
 */
export async function getUserVideoPlatforms(
  firebaseUid: string
): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('video_analysis_runs')
      .select('video_platform')
      .eq('firebase_uid', firebaseUid)
      .not('video_platform', 'is', null);

    if (error) {
      console.error('Error getting user video platforms:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Get distinct platforms
    const platformValues = data.map((row: any) => row.video_platform).filter(Boolean) as string[];
    const platforms = Array.from(new Set(platformValues));
    
    return platforms;
  } catch (error) {
    console.error('Error in getUserVideoPlatforms:', error);
    return [];
  }
}

/**
 * Generate a preview URL for a video
 * Uses backend API proxy to avoid CORS issues with Google Cloud Storage
 */
export function getPreviewUrl(userId: string, videoName: string): string {
  // Use backend API proxy to serve preview images
  // This avoids CORS issues that occur when loading directly from GCS
  return `${API_BASE_URL}/api/v1/users/video-preview/${userId}/${encodeURIComponent(videoName)}`;
}

/**
 * Clear image proxy cache
 * Use when logging out or switching users
 */
export function clearImageProxyCache(): void {
  imageProxyCache.clear();
}

/**
 * ✅ NEW: Clear dashboard videos cache
 * Call when user wants to manually refresh data
 */
export function clearDashboardVideosCache(userId?: string): void {
  if (userId) {
    // Clear cache entries for specific user
    const keysToDelete: string[] = [];
    dashboardVideosCache.forEach((value, key) => {
      if (value.userId === userId) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => dashboardVideosCache.delete(key));
    console.log(`✅ Cleared dashboard cache for user ${userId}`);
  } else {
    // Clear all cache
    dashboardVideosCache.clear();
    imageUrlCache.clear();
    console.log('✅ Cleared all cache');
  }
}

/**
 * ✅ NEW: Get cache stats for debugging
 */
export function getCacheStats() {
  const now = Date.now();
  const dashboardEntries = Array.from(dashboardVideosCache.entries()).map(([key, value]) => ({
    key,
    userId: value.userId,
    age: Math.round((now - value.timestamp) / 1000) + 's',
    videos: value.data.length,
    expired: now - value.timestamp > CACHE_DURATION
  }));
  
  const imageEntries = Array.from(imageUrlCache.entries()).map(([key, value]) => ({
    key,
    age: Math.round((now - value.timestamp) / 1000) + 's',
    expired: now - value.timestamp > IMAGE_CACHE_DURATION
  }));
  
  return {
    dashboardVideos: dashboardEntries,
    imageUrls: imageEntries,
    totalCacheSize: dashboardEntries.length + imageEntries.length
  };
}

/**
 * ✅ NEW: Start cache cleanup on app startup
 */
export function initializeCacheManagement(): void {
  startCacheCleanup();
}

/**
 * Count completed deep-video analyses for a user (excludes forensic runs).
 */
export async function countUserCompletedAnalyses(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('video_analysis_runs')
    .select('*', { count: 'exact', head: true })
    .eq('firebase_uid', userId)
    .eq('status', 'completed')
    .neq('video_source_type', 'forensic');

  if (error) {
    console.error('countUserCompletedAnalyses:', error);
    return -1;
  }
  return count ?? 0;
}

/**
 * True when the user has exactly one completed analysis (first-ever result).
 * Retries briefly so callers can run right as a run flips to completed.
 */
export async function isFirstCompletedAnalysis(
  userId: string,
  maxAttempts = 4,
  delayMs = 1500
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const count = await countUserCompletedAnalyses(userId);
    if (count === 1) return true;
    if (count > 1) return false;
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return false;
}

/**
 * ✅ NEW: Force refresh dashboard data when analysis completes
 * Call this when a video analysis finishes to ensure fresh data
 */
export function invalidateDashboardCache(userId: string): void {
  // Clear all cache entries for this user
  clearDashboardVideosCache(userId);
  
  // Also clear image cache for this user to refresh thumbnails
  const keysToDelete: string[] = [];
  imageUrlCache.forEach((value, key) => {
    if (key.startsWith(userId)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => imageUrlCache.delete(key));
  
  console.log(`🔄 Invalidated dashboard cache for user ${userId} - fresh data will be fetched`);
}

/**
 * ✅ NEW: Cleanup on app shutdown
 */
export function shutdownCacheManagement(): void {
  stopCacheCleanup();
}

export const VideoAnalysisSupabaseService = {
  getDashboardVideos,
  getRecentAnalyses,
  getDashboardStats,
  countUserCompletedAnalyses,
  isFirstCompletedAnalysis,
  getVideoAnalysisDetail,
  getPreviewUrl,
  getPreviewUrlWithFallback,
  clearImageProxyCache,
  clearDashboardVideosCache,
  getCacheStats,
  initializeCacheManagement,
  shutdownCacheManagement,
};
